// Stats.jsx - Statistics Page with Comprehensive Comparison
// Integrates StatsComparison for versus-style detailed comparison
// Updated: Added Call Logs tab with hierarchical filtering

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Phone, PhoneForwarded, PhoneOff,
    Bot, Mic, Users, Clock,
    BarChart3, RefreshCw, Filter, X, Calendar,
    ChevronDown, Check, GitCompare, ArrowLeft,
    FileText, Trophy, TrendingUp, AlertTriangle  // Added for Calls tab and Voice Sets
} from 'lucide-react'
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, Tooltip,
    CartesianGrid
} from 'recharts'
import { monitoringApi, dialerApi, voiceSetApi, avatarApi } from '../services/api'
import StatsComparison from '../components/StatsComparison'
import CallLogsTab from '../components/CallLogsTab'
import './Stats.css'
import '../components/CallLogsTab.css'

// Disposition labels
const DISPOSITION_LABELS = {
    'not-interested': 'Not Interested',
    'not_interested': 'Not Interested',
    'answering-machine': 'Answering Machine',
    'answering_machine': 'Answering Machine',
    'do-not-call': 'Do Not Call',
    'do_not_call': 'Do Not Call',
    'no-answer': 'No Answer',
    'no_answer': 'No Answer',
    'call-back': 'Call Back',
    'call_back': 'Call Back',
    'transfer-call': 'Transfer',
    'call-transfered': 'Transferred',
    'TRANSFER': 'Transfer',
}

const TRANSFER_DISPOSITIONS = ['transfer-call', 'call-transfered', 'TRANSFER', 'transfer', 'xfer']

const formatLabel = (str) => {
    if (!str) return 'Unknown'
    if (DISPOSITION_LABELS[str]) return DISPOSITION_LABELS[str]
    return str.replace(/[-_]/g, ' ').split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

const formatDuration = (seconds) => {
    if (!seconds) return '0s'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins > 0) return `${mins}m ${secs}s`
    return `${secs}s`
}

const formatCompact = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
    if (num >= 10000) return Math.round(num / 1000) + 'K'
    return num.toLocaleString()
}

const PIE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#6366f1', '#ec4899', '#14b8a6']
const COMPARE_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#f59e0b']

const getDateString = (date) => date.toISOString().split('T')[0]
const getYesterday = () => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d
}

export default function Stats() {
    // URL Params for tab and search
    const [searchParams, setSearchParams] = useSearchParams()
    const urlTab = searchParams.get('tab')
    const urlSearch = searchParams.get('search')
    const urlLicenseId = searchParams.get('license_id')

    // Filter options data
    const [dialers, setDialers] = useState([])
    const [voiceSets, setVoiceSets] = useState([])
    const [avatars, setAvatars] = useState([])
    const [allBots, setAllBots] = useState([])

    // Date state
    const [datePreset, setDatePreset] = useState('week')
    const [customDateFrom, setCustomDateFrom] = useState('')
    const [customDateTo, setCustomDateTo] = useState('')
    const [showDatePicker, setShowDatePicker] = useState(false)

    // Filter state
    const [selectedDialer, setSelectedDialer] = useState(null)
    const [selectedAvatar, setSelectedAvatar] = useState(null)
    const [selectedVoiceSet, setSelectedVoiceSet] = useState(null)
    const [selectedSips, setSelectedSips] = useState([])
    const [showCompareMenu, setShowCompareMenu] = useState(false)
    const [showSipSelector, setShowSipSelector] = useState(false)

    // Comparison mode
    const [compareMode, setCompareMode] = useState(null) // 'dialer' | 'avatar' | 'sip' | null
    const [compareItems, setCompareItems] = useState([])
    const [showComparison, setShowComparison] = useState(false)

    // Stats data
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
    const [overview, setOverview] = useState(null)
    const [dispositionStats, setDispositionStats] = useState(null)
    const [avatarStats, setAvatarStats] = useState([])
    const [voiceSetStats, setVoiceSetStats] = useState([])
    const [dialerStats, setDialerStats] = useState([])
    const [showAllDialers, setShowAllDialers] = useState(false)
    const [showAllAvatars, setShowAllAvatars] = useState(false)
    const [showAllAttention, setShowAllAttention] = useState(false)

    // Tab state - read from URL or default to 'overview'
    const [activeTab, setActiveTab] = useState(urlTab === 'calls' ? 'calls' : 'overview')

    // Get filtered SIPs based on selected dialer
    const filteredSips = useMemo(() => {
        if (!selectedDialer) return allBots
        return allBots.filter(b => b.dialer_id === selectedDialer.id)
    }, [allBots, selectedDialer])

    // Build date params
    const getDateParams = useCallback(() => {
        const today = new Date()

        if (datePreset === 'custom' && customDateFrom && customDateTo) {
            return { from: customDateFrom, to: customDateTo }
        }

        switch (datePreset) {
            case 'today':
                return { from: getDateString(today), to: getDateString(today) }
            case 'yesterday':
                const yesterday = getYesterday()
                return { from: getDateString(yesterday), to: getDateString(yesterday) }
            case 'week':
                const weekAgo = new Date(today - 7 * 24 * 60 * 60 * 1000)
                return { from: getDateString(weekAgo), to: getDateString(today) }
            case 'month':
                const monthAgo = new Date(today - 30 * 24 * 60 * 60 * 1000)
                return { from: getDateString(monthAgo), to: getDateString(today) }
            default:
                return { from: getDateString(today), to: getDateString(today) }
        }
    }, [datePreset, customDateFrom, customDateTo])

    // Build query params with filters
    const buildParams = useCallback(() => {
        const params = getDateParams()
        if (selectedDialer) params.dialer_id = selectedDialer.id
        if (selectedAvatar) params.avatar_id = selectedAvatar.id
        if (selectedVoiceSet) params.voice_set_id = selectedVoiceSet.id
        if (selectedSips.length === 1) params.sip_id = selectedSips[0]
        return params
    }, [getDateParams, selectedDialer, selectedAvatar, selectedVoiceSet, selectedSips])

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [dialersRes, voiceSetsRes, avatarsRes, botsRes] = await Promise.allSettled([
                    dialerApi.getAll(),
                    voiceSetApi.getAll(),
                    avatarApi?.getAll() || Promise.resolve({ data: [] }),
                    monitoringApi.getAllStatus()
                ])

                if (dialersRes.status === 'fulfilled') setDialers(dialersRes.value.data || [])
                if (voiceSetsRes.status === 'fulfilled') setVoiceSets(voiceSetsRes.value.data || [])
                if (avatarsRes.status === 'fulfilled') setAvatars(avatarsRes.value.data || [])
                if (botsRes.status === 'fulfilled') setAllBots(botsRes.value.data || [])
            } catch (error) {
                console.error('Failed to load filter options:', error)
            }
        }
        loadFilterOptions()
    }, [])

    // Calculate transfers from dispositions (fallback)
    const calculateTransfersFromDispositions = (dispositions) => {
        if (!dispositions) return 0
        let count = 0
        dispositions.forEach(d => {
            const dispo = (d.disposition || '').toLowerCase()
            if (TRANSFER_DISPOSITIONS.some(t => dispo.includes(t.toLowerCase()))) {
                count += parseInt(d.count) || 0
            }
        })
        return count
    }

    // Fetch stats
    const fetchStats = useCallback(async (showRefreshing = false) => {
        if (showComparison) return

        try {
            if (showRefreshing) setRefreshing(true)
            else setLoading(true)

            const params = buildParams()

            const [overviewRes, dispoRes, avatarRes, voiceSetRes, dialerRes] = await Promise.allSettled([
                monitoringApi.getStatsOverview(params),
                monitoringApi.getDispositionStats(params),
                monitoringApi.getStatsByAvatar(params),
                monitoringApi.getStatsByVoiceSet(params),
                monitoringApi.getStatsByDialer(params)
            ])

            if (overviewRes.status === 'fulfilled') {
                setOverview(overviewRes.value.data?.overview || overviewRes.value.data)
            }

            if (dispoRes.status === 'fulfilled') {
                const data = dispoRes.value.data
                if (data && data.transfers?.count === 0 && data.dispositions) {
                    const fallback = calculateTransfersFromDispositions(data.dispositions)
                    if (fallback > 0) {
                        data.transfers = {
                            count: fallback,
                            percentage: data.total_calls > 0 ? parseFloat(((fallback / data.total_calls) * 100).toFixed(1)) : 0
                        }
                    }
                }
                setDispositionStats(data)
            }

            if (avatarRes.status === 'fulfilled') setAvatarStats(avatarRes.value.data?.avatars || [])
            if (voiceSetRes.status === 'fulfilled') setVoiceSetStats(voiceSetRes.value.data?.voice_sets || [])
            if (dialerRes.status === 'fulfilled') setDialerStats(dialerRes.value.data?.dialers || [])
        } catch (error) {
            console.error('Stats fetch error:', error)
        } finally {
            setLoading(false)
            setRefreshing(false)
            setHasInitiallyLoaded(true)
        }
    }, [buildParams, showComparison])

    // Initial fetch
    useEffect(() => {
        fetchStats()
    }, [fetchStats])

    // Silent auto-refresh every 30 seconds (doesn't affect scroll position)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchStats(true) // true = silent refresh, only updates data without loading state
        }, 30000)
        return () => clearInterval(interval)
    }, [fetchStats])

    // Filter handlers
    const handleDialerClick = (dialer) => {
        if (compareMode === 'dialer') {
            toggleCompareItem(dialer)
        } else {
            setSelectedDialer(selectedDialer?.id === dialer.id ? null : dialer)
            setSelectedSips([])
        }
    }

    const handleAvatarClick = (avatar) => {
        if (compareMode === 'avatar') {
            toggleCompareItem(avatar)
        } else {
            setSelectedAvatar(selectedAvatar?.id === avatar.id ? null : avatar)
        }
    }

    const handleVoiceSetClick = (voiceSet) => {
        setSelectedVoiceSet(selectedVoiceSet?.id === voiceSet.id ? null : voiceSet)
    }

    const toggleSipSelection = (sip) => {
        if (compareMode === 'sip') {
            toggleCompareItem(sip)
        } else {
            setSelectedSips(prev =>
                prev.includes(sip.sip_id)
                    ? prev.filter(s => s !== sip.sip_id)
                    : [...prev, sip.sip_id]
            )
        }
    }

    const toggleCompareItem = (item) => {
        const itemId = item.id || item.sip_id
        const exists = compareItems.find(i => (i.id || i.sip_id) === itemId)

        if (exists) {
            setCompareItems(prev => prev.filter(i => (i.id || i.sip_id) !== itemId))
        } else if (compareItems.length < 4) {
            setCompareItems(prev => [...prev, item])
        }
    }

    const startCompareMode = (mode) => {
        setCompareMode(mode)
        setCompareItems([])
        setShowCompareMenu(false)
    }

    const exitCompareMode = () => {
        setCompareMode(null)
        setCompareItems([])
        setShowComparison(false)
    }

    const launchComparison = () => {
        if (compareItems.length >= 2) {
            setShowComparison(true)
        }
    }

    const clearAllFilters = () => {
        setSelectedDialer(null)
        setSelectedAvatar(null)
        setSelectedVoiceSet(null)
        setSelectedSips([])
    }

    const hasActiveFilters = selectedDialer || selectedAvatar || selectedVoiceSet || selectedSips.length > 0

    // Chart data
    const pieChartData = dispositionStats?.dispositions?.map((d, i) => ({
        name: formatLabel(d.disposition),
        value: d.count,
        percentage: d.percentage,
        color: PIE_COLORS[i % PIE_COLORS.length]
    })) || []

    const avatarChartData = avatarStats.slice(0, 8).map(a => ({
        name: a.avatar_name?.substring(0, 12) || `Avatar ${a.avatar_id}`,
        calls: a.total_calls,
        transfers: a.transfers
    }))

    const transferStats = overview?.transfers || dispositionStats?.transfers || { count: 0, percentage: 0 }

    const getDateLabel = () => {
        switch (datePreset) {
            case 'today': return 'Today'
            case 'yesterday': return 'Yesterday'
            case 'week': return 'Last 7 Days'
            case 'month': return 'Last 30 Days'
            case 'custom':
                if (customDateFrom && customDateTo) return `${customDateFrom} - ${customDateTo}`
                return 'Custom Range'
            default: return 'Select Date'
        }
    }

    // Show comprehensive comparison view
    if (showComparison && compareItems.length >= 2) {
        return (
            <StatsComparison
                mode={compareMode}
                items={compareItems}
                dateParams={getDateParams()}
                onBack={exitCompareMode}
            />
        )
    }

    // Compare mode - item selection view
    if (compareMode) {
        return (
            <div className="stats-page">
                <div className="stats-header">
                    <div className="stats-header-left">
                        <button className="back-btn" onClick={exitCompareMode}>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="stats-title">
                            <GitCompare size={28} />
                            Compare {compareMode === 'dialer' ? 'Dialers' : compareMode === 'avatar' ? 'Avatars' : 'SIPs'}
                        </h1>
                        <span className="compare-hint">Select 2-4 items to compare</span>
                    </div>
                    <div className="stats-header-right">
                        <div className="date-dropdown">
                            <button className="date-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
                                <Calendar size={16} />
                                {getDateLabel()}
                                <ChevronDown size={16} />
                            </button>
                            {showDatePicker && (
                                <DatePickerDropdown
                                    datePreset={datePreset}
                                    setDatePreset={setDatePreset}
                                    customDateFrom={customDateFrom}
                                    setCustomDateFrom={setCustomDateFrom}
                                    customDateTo={customDateTo}
                                    setCustomDateTo={setCustomDateTo}
                                    onClose={() => setShowDatePicker(false)}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Selection chips */}
                <div className="compare-selection">
                    <div className="selection-chips">
                        {compareItems.length === 0 && (
                            <span className="selection-placeholder">No items selected</span>
                        )}
                        {compareItems.map((item, i) => (
                            <motion.div
                                key={item.id || item.sip_id || i}
                                className="selection-chip"
                                style={{ '--chip-color': COMPARE_COLORS[i] }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                            >
                                <span className="chip-color" style={{ background: COMPARE_COLORS[i] }}></span>
                                {item.name || item.sip_id}
                                <button onClick={() => toggleCompareItem(item)}>
                                    <X size={14} />
                                </button>
                            </motion.div>
                        ))}
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={launchComparison}
                        disabled={compareItems.length < 2}
                    >
                        <GitCompare size={16} />
                        Compare {compareItems.length >= 2 ? `(${compareItems.length})` : ''}
                    </button>
                </div>

                {/* Items to select */}
                <div className="compare-items-grid">
                    {compareMode === 'dialer' && dialers.map(d => (
                        <CompareItemCard
                            key={d.id}
                            item={d}
                            selected={compareItems.some(i => i.id === d.id)}
                            color={COMPARE_COLORS[compareItems.findIndex(i => i.id === d.id)] || null}
                            onClick={() => toggleCompareItem(d)}
                            disabled={compareItems.length >= 4 && !compareItems.some(i => i.id === d.id)}
                        />
                    ))}
                    {compareMode === 'avatar' && avatars.map(a => (
                        <CompareItemCard
                            key={a.id}
                            item={a}
                            selected={compareItems.some(i => i.id === a.id)}
                            color={COMPARE_COLORS[compareItems.findIndex(i => i.id === a.id)] || null}
                            onClick={() => toggleCompareItem(a)}
                            disabled={compareItems.length >= 4 && !compareItems.some(i => i.id === a.id)}
                        />
                    ))}
                    {compareMode === 'sip' && allBots.map(b => (
                        <CompareItemCard
                            key={b.sip_id}
                            item={{ name: b.sip_id, id: b.sip_id, sip_id: b.sip_id, ...b }}
                            selected={compareItems.some(i => i.sip_id === b.sip_id)}
                            color={COMPARE_COLORS[compareItems.findIndex(i => i.sip_id === b.sip_id)] || null}
                            onClick={() => toggleCompareItem({ name: b.sip_id, id: b.sip_id, sip_id: b.sip_id, ...b })}
                            disabled={compareItems.length >= 4 && !compareItems.some(i => i.sip_id === b.sip_id)}
                            subtitle={b.company_name || b.dialer_name}
                        />
                    ))}
                </div>
            </div>
        )
    }

    // Main stats view
    if (loading) {
        return (
            <div className="stats-page">
                <div className="stats-loading">
                    <div className="spinner"></div>
                    <p>Loading statistics...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="stats-page">
            {/* Header */}
            <div className="stats-header">
                <div className="stats-header-left">
                    <h1 className="stats-title">
                        <BarChart3 size={28} />
                        Statistics
                    </h1>
                </div>
                <div className="stats-header-right">
                    <div className="date-dropdown">
                        <button className="date-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
                            <Calendar size={16} />
                            {getDateLabel()}
                            <ChevronDown size={16} />
                        </button>
                        {showDatePicker && (
                            <DatePickerDropdown
                                datePreset={datePreset}
                                setDatePreset={setDatePreset}
                                customDateFrom={customDateFrom}
                                setCustomDateFrom={setCustomDateFrom}
                                customDateTo={customDateTo}
                                setCustomDateTo={setCustomDateTo}
                                onClose={() => setShowDatePicker(false)}
                            />
                        )}
                    </div>

                    <div className="compare-dropdown">
                        <button className="compare-btn" onClick={() => setShowCompareMenu(!showCompareMenu)}>
                            <GitCompare size={16} />
                            Compare
                            <ChevronDown size={16} />
                        </button>
                        {showCompareMenu && (
                            <div className="dropdown-menu">
                                <button onClick={() => startCompareMode('dialer')}>
                                    <Phone size={16} /> Compare Dialers
                                </button>
                                <button onClick={() => startCompareMode('avatar')}>
                                    <Users size={16} /> Compare Avatars
                                </button>
                                <button onClick={() => startCompareMode('sip')}>
                                    <Bot size={16} /> Compare SIPs
                                </button>
                            </div>
                        )}
                    </div>

                    <button className="refresh-btn" onClick={() => fetchStats(true)} disabled={refreshing}>
                        <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
                    </button>
                </div>
            </div>

            {/* NEW: Tab Navigation */}
            <div className="stats-tabs">
                <button
                    className={`stats-tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    <BarChart3 size={16} />
                    Overview
                </button>
                <button
                    className={`stats-tab ${activeTab === 'calls' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calls')}
                >
                    <FileText size={16} />
                    Calls
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' ? (
                <>
                    {/* Active Filters */}
                    {hasActiveFilters && (
                        <div className="active-filters">
                            <span className="filters-label">Filtering by:</span>
                            {selectedDialer && (
                                <div className="filter-tag">
                                    <Phone size={14} />
                                    {selectedDialer.name}
                                    <button onClick={() => setSelectedDialer(null)}><X size={12} /></button>
                                </div>
                            )}
                            {selectedAvatar && (
                                <div className="filter-tag">
                                    <Users size={14} />
                                    {selectedAvatar.name}
                                    <button onClick={() => setSelectedAvatar(null)}><X size={12} /></button>
                                </div>
                            )}
                            {selectedVoiceSet && (
                                <div className="filter-tag">
                                    <Mic size={14} />
                                    {selectedVoiceSet.name}
                                    <button onClick={() => setSelectedVoiceSet(null)}><X size={12} /></button>
                                </div>
                            )}
                            {selectedSips.map(sip => (
                                <div key={sip} className="filter-tag">
                                    <Bot size={14} />
                                    {sip}
                                    <button onClick={() => setSelectedSips(prev => prev.filter(s => s !== sip))}><X size={12} /></button>
                                </div>
                            ))}
                            <button className="clear-all-btn" onClick={clearAllFilters}>Clear All</button>
                        </div>
                    )}

                    {/* KPI Cards */}
                    <div className="stat-blocks">
                        <KpiCard icon={<Phone size={18} />} label="Total Calls" value={overview?.total_calls || 0} colorClass="blue" skipAnimation={hasInitiallyLoaded} />
                        <KpiCard icon={<PhoneForwarded size={18} />} label="Transfers" value={transferStats.count} subValue={`${transferStats.percentage}% rate`} colorClass="green" skipAnimation={hasInitiallyLoaded} />
                        <KpiCard icon={<Bot size={18} />} label="Ans. Machines" value={overview?.answering_machines?.count || 0} subValue={`${overview?.answering_machines?.percentage || 0}%`} colorClass="orange" skipAnimation={hasInitiallyLoaded} />
                        <KpiCard icon={<PhoneOff size={18} />} label="Not Interested" value={overview?.not_interested?.count || 0} subValue={`${overview?.not_interested?.percentage || 0}%`} colorClass="red" skipAnimation={hasInitiallyLoaded} />
                        <KpiCard icon={<Clock size={18} />} label="Avg Duration" value={formatDuration(overview?.avg_duration || 0)} subValue={`${overview?.avg_steps || 0} steps`} colorClass="purple" skipAnimation={hasInitiallyLoaded} />
                    </div>

                    {/* Charts Row */}
                    <div className="charts-row">
                        <div className="chart-card">
                            <h3 className="chart-title">Disposition Breakdown</h3>
                            {pieChartData.length > 0 ? (
                                <div className="chart-with-legend">
                                    <div className="pie-chart-container">
                                        <ResponsiveContainer width="100%" height={220}>
                                            <PieChart>
                                                <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">
                                                    {pieChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomPieTooltip />} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="chart-legend">
                                        {pieChartData.slice(0, 6).map((item, i) => (
                                            <div key={i} className="legend-item">
                                                <span className="legend-color" style={{ background: item.color }}></span>
                                                <span className="legend-label">{item.name}</span>
                                                <span className="legend-value">{formatCompact(item.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="no-data">No disposition data</div>
                            )}
                        </div>

                        <div className="chart-card">
                            <h3 className="chart-title">Avatar Performance</h3>
                            {avatarChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={avatarChartData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                                        <YAxis dataKey="name" type="category" width={85} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                                        <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }} />
                                        <Bar dataKey="calls" fill="var(--text-muted)" name="Total Calls" radius={[0, 4, 4, 0]} />
                                        <Bar dataKey="transfers" fill="var(--success)" name="Transfers" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="no-data">No avatar data</div>
                            )}
                        </div>
                    </div>

                    {/* Dashboard Panels */}
                    <div className="stats-panels">
                        {/* Dialers Panel */}
                        <div className="stats-panel">
                            <div className="stats-panel__header">
                                <h3><Phone size={18} /> Dialers <span className="stats-panel__count">{dialerStats.length}</span></h3>
                                <div className="stats-panel__header-actions">
                                    <button className={`stats-panel__toggle ${showAllDialers ? 'active' : ''}`} onClick={() => setShowAllDialers(!showAllDialers)}>
                                        {showAllDialers ? 'Top 8' : 'Show All'}
                                    </button>
                                    <button className="stats-panel__action" onClick={() => startCompareMode('dialer')}>
                                        <GitCompare size={14} /> Compare
                                    </button>
                                </div>
                            </div>
                            <div className={`stats-panel__list ${showAllDialers ? 'expanded' : ''}`}>
                                {[...dialerStats].sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0)).slice(0, showAllDialers ? undefined : 8).map((row, i) => {
                                    const maxRate = [...dialerStats].sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0))[0]?.transfer_rate || 1
                                    const barWidth = (row.transfer_rate / maxRate) * 100
                                    return (
                                        <div
                                            key={row.dialer_id}
                                            className={`stats-panel__item ${selectedDialer?.id === row.dialer_id ? 'selected' : ''} ${i === 0 ? 'top' : ''}`}
                                            onClick={() => handleDialerClick({ id: row.dialer_id, name: row.dialer_name })}
                                        >
                                            <div className="stats-panel__item-bar" style={{ width: `${barWidth}%` }} />
                                            <div className="stats-panel__item-content">
                                                <span className="stats-panel__item-rank">{i + 1}</span>
                                                <span className="stats-panel__item-name">{row.dialer_name || `Dialer ${row.dialer_id}`}</span>
                                                <div className="stats-panel__item-metrics">
                                                    <span className="metric">{row.total_calls.toLocaleString()} <small>calls</small></span>
                                                    <span className="metric success">{row.transfers} <small>xfer</small></span>
                                                    <span className={`metric-rate ${row.transfer_rate >= 5 ? 'good' : row.transfer_rate >= 2 ? 'mid' : 'low'}`}>
                                                        {row.transfer_rate}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {dialerStats.length === 0 && <div className="stats-panel__empty">No dialer data</div>}
                            </div>
                        </div>

                        {/* Avatars Panel */}
                        <div className="stats-panel">
                            <div className="stats-panel__header">
                                <h3><Users size={18} /> Avatars <span className="stats-panel__count">{avatarStats.length}</span></h3>
                                <div className="stats-panel__header-actions">
                                    <button className={`stats-panel__toggle ${showAllAvatars ? 'active' : ''}`} onClick={() => setShowAllAvatars(!showAllAvatars)}>
                                        {showAllAvatars ? 'Top 8' : 'Show All'}
                                    </button>
                                    <button className="stats-panel__action" onClick={() => startCompareMode('avatar')}>
                                        <GitCompare size={14} /> Compare
                                    </button>
                                </div>
                            </div>
                            <div className={`stats-panel__list ${showAllAvatars ? 'expanded' : ''}`}>
                                {[...avatarStats].sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0)).slice(0, showAllAvatars ? undefined : 8).map((row, i) => {
                                    const maxRate = [...avatarStats].sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0))[0]?.transfer_rate || 1
                                    const barWidth = (row.transfer_rate / maxRate) * 100
                                    return (
                                        <div
                                            key={row.avatar_id}
                                            className={`stats-panel__item ${selectedAvatar?.id === row.avatar_id ? 'selected' : ''} ${i === 0 ? 'top' : ''}`}
                                            onClick={() => handleAvatarClick({ id: row.avatar_id, name: row.avatar_name })}
                                        >
                                            <div className="stats-panel__item-bar" style={{ width: `${barWidth}%` }} />
                                            <div className="stats-panel__item-content">
                                                <span className="stats-panel__item-rank">{i + 1}</span>
                                                <span className="stats-panel__item-name">{row.avatar_name || `Avatar ${row.avatar_id}`}</span>
                                                <div className="stats-panel__item-metrics">
                                                    <span className="metric">{row.total_calls.toLocaleString()} <small>calls</small></span>
                                                    <span className="metric success">{row.transfers} <small>xfer</small></span>
                                                    <span className={`metric-rate ${row.transfer_rate >= 5 ? 'good' : row.transfer_rate >= 2 ? 'mid' : 'low'}`}>
                                                        {row.transfer_rate}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {avatarStats.length === 0 && <div className="stats-panel__empty">No avatar data</div>}
                            </div>
                        </div>

                        {/* Worst Performing Dialers Panel */}
                        <div className="stats-panel stats-panel--worst">
                            <div className="stats-panel__header">
                                <h3><AlertTriangle size={18} /> Needs Attention</h3>
                                <div className="stats-panel__header-actions">
                                    <button className={`stats-panel__toggle ${showAllAttention ? 'active' : ''}`} onClick={() => setShowAllAttention(!showAllAttention)}>
                                        {showAllAttention ? 'Top 8' : 'Show All'}
                                    </button>
                                    <span className="stats-panel__subtitle">Lowest transfer rate</span>
                                </div>
                            </div>
                            <div className={`stats-panel__list ${showAllAttention ? 'expanded' : ''}`}>
                                {[...dialerStats]
                                    .filter(d => d.total_calls >= 100) // Only show dialers with meaningful call volume
                                    .sort((a, b) => (a.transfer_rate || 0) - (b.transfer_rate || 0)) // Lowest first
                                    .slice(0, showAllAttention ? undefined : 8)
                                    .map((row, i) => {
                                    const worstRate = [...dialerStats].filter(d => d.total_calls >= 100).sort((a, b) => (a.transfer_rate || 0) - (b.transfer_rate || 0))[0]?.transfer_rate || 0
                                    const bestRate = Math.max(...dialerStats.filter(d => d.total_calls >= 100).map(d => d.transfer_rate || 0), 1)
                                    const barWidth = bestRate > 0 ? ((row.transfer_rate || 0) / bestRate) * 100 : 0
                                    return (
                                        <div
                                            key={row.dialer_id}
                                            className={`stats-panel__item stats-panel__item--worst ${selectedDialer?.id === row.dialer_id ? 'selected' : ''}`}
                                            onClick={() => handleDialerClick({ id: row.dialer_id, name: row.dialer_name })}
                                        >
                                            <div className="stats-panel__item-bar stats-panel__item-bar--worst" style={{ width: `${barWidth}%` }} />
                                            <div className="stats-panel__item-content">
                                                <span className="stats-panel__item-rank">{i + 1}</span>
                                                <span className="stats-panel__item-name">{row.dialer_name || `Dialer ${row.dialer_id}`}</span>
                                                <div className="stats-panel__item-metrics">
                                                    <span className="metric">{row.total_calls.toLocaleString()} <small>calls</small></span>
                                                    <span className="metric">{row.transfers} <small>xfer</small></span>
                                                    <span className={`metric-rate ${row.transfer_rate >= 5 ? 'good' : row.transfer_rate >= 2 ? 'mid' : 'low'}`}>
                                                        {row.transfer_rate}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {dialerStats.filter(d => d.total_calls >= 100).length === 0 && <div className="stats-panel__empty">No dialers with 100+ calls</div>}
                            </div>
                        </div>
                    </div>

                    {/* Voice Sets Performance Panel */}
                    <div className="voice-sets-section">
                        <div className="voice-sets-header">
                            <h2><Mic size={22} /> Voice Sets Performance</h2>
                            <span className="voice-sets-subtitle">Ranked by avg duration • Top {Math.min(voiceSetStats.filter(vs => vs.total_calls > 0).length, 12)}</span>
                        </div>
                        <div className="voice-sets-grid">
                            {voiceSetStats
                                .filter(vs => vs.total_calls > 0)
                                .sort((a, b) => (b.avg_duration || 0) - (a.avg_duration || 0))
                                .slice(0, 12)
                                .map((vs, index) => (
                                <VoiceSetCard
                                    key={vs.voice_set_id || index}
                                    voiceSet={vs}
                                    rank={index + 1}
                                    isTop={index === 0}
                                    onClick={() => handleVoiceSetClick({ id: vs.voice_set_id, name: vs.voice_set_name })}
                                    selected={selectedVoiceSet?.id === vs.voice_set_id}
                                    skipAnimation={hasInitiallyLoaded}
                                />
                            ))}
                            {voiceSetStats.filter(vs => vs.total_calls > 0).length === 0 && (
                                <div className="voice-sets-empty">No voice set data available for this period</div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                /* Call Logs Tab Content - Pass memoized dates and initial search from URL */
                <CallLogsTab
                    key={`${datePreset}-${customDateFrom}-${customDateTo}-${urlSearch}-${urlLicenseId}`}
                    dateFrom={buildParams().from || getDateParams().from}
                    dateTo={buildParams().to || getDateParams().to}
                    initialSearch={urlSearch || ''}
                    initialLicenseId={urlLicenseId || ''}
                />
            )}
        </div>
    )
}

// Components
function KpiCard({ icon, label, value, subValue, colorClass = 'blue', skipAnimation = false }) {
    return (
        <motion.div
            className={`stat-block ${colorClass}`}
            initial={skipAnimation ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <div className="stat-block-icon">{icon}</div>
            <div className="stat-block-content">
                <span className="stat-block-value">{value}</span>
                <span className="stat-block-label">{label}</span>
            </div>
        </motion.div>
    )
}

function DatePickerDropdown({ datePreset, setDatePreset, customDateFrom, setCustomDateFrom, customDateTo, setCustomDateTo, onClose }) {
    return (
        <motion.div className="stats-date-picker" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="stats-date-picker__presets">
                {[
                    { value: 'today', label: 'Today' },
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'week', label: 'Last 7 Days' },
                    { value: 'month', label: 'Last 30 Days' },
                ].map(p => (
                    <button
                        key={p.value}
                        className={datePreset === p.value ? 'active' : ''}
                        onClick={() => { setDatePreset(p.value); onClose(); }}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="stats-date-picker__divider">or select custom range</div>
            <div className="stats-date-picker__inputs">
                <input type="date" value={customDateFrom} onChange={(e) => { setCustomDateFrom(e.target.value); setDatePreset('custom'); }} />
                <span>to</span>
                <input type="date" value={customDateTo} onChange={(e) => { setCustomDateTo(e.target.value); setDatePreset('custom'); }} />
            </div>
            {datePreset === 'custom' && customDateFrom && customDateTo && (
                <button className="stats-date-picker__apply" onClick={onClose}>Apply</button>
            )}
        </motion.div>
    )
}

function CompareItemCard({ item, selected, color, onClick, disabled, subtitle }) {
    return (
        <motion.div
            className={`compare-item-card ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            style={selected ? { '--item-color': color } : {}}
            onClick={disabled ? undefined : onClick}
            whileHover={disabled ? {} : { scale: 1.02 }}
            whileTap={disabled ? {} : { scale: 0.98 }}
        >
            {selected && <div className="selected-indicator" style={{ background: color }}></div>}
            <div className="item-info">
                <div className="item-name">{item.name}</div>
                {subtitle && <div className="item-subtitle">{subtitle}</div>}
            </div>
            {selected && <Check size={18} style={{ color }} />}
        </motion.div>
    )
}

function CustomPieTooltip({ active, payload }) {
    if (active && payload && payload.length) {
        const data = payload[0].payload
        return (
            <div className="chart-tooltip">
                <p className="tooltip-label">{data.name}</p>
                <p className="tooltip-value">{data.value} calls ({data.percentage}%)</p>
            </div>
        )
    }
    return null
}

// Voice Set disposition colors
const VS_COLORS = {
    transfer: '#22c55e',
    answering_machine: '#f59e0b',
    not_interested: '#ef4444',
    no_answer: '#6b7280',
    hang_up: '#dc2626',
    do_not_call: '#7c3aed',
    other: '#94a3b8'
}

const VS_LABELS = {
    transfer: 'Transfers',
    answering_machine: 'Ans. Machine',
    not_interested: 'Not Interested',
    no_answer: 'No Answer',
    hang_up: 'Hang Up',
    do_not_call: 'DNC',
    other: 'Other'
}

function VoiceSetCard({ voiceSet, rank, isTop, onClick, selected, skipAnimation = false }) {
    const { voice_set_name, total_calls, transfers, transfer_rate, avg_duration, dispositions } = voiceSet

    // Build pie chart data from dispositions
    const pieData = dispositions ? Object.entries(dispositions)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => ({
            name: VS_LABELS[key] || key,
            value: value,
            percentage: total_calls > 0 ? ((value / total_calls) * 100).toFixed(1) : 0,
            color: VS_COLORS[key] || '#94a3b8'
        }))
        .sort((a, b) => b.value - a.value) : []

    const getRankBadge = () => {
        if (rank === 1) return <span className="vs-rank gold"><Trophy size={14} /> #1</span>
        if (rank === 2) return <span className="vs-rank silver">#2</span>
        if (rank === 3) return <span className="vs-rank bronze">#3</span>
        return <span className="vs-rank">#{rank}</span>
    }

    return (
        <motion.div
            className={`voice-set-card ${isTop ? 'top-performer' : ''} ${selected ? 'selected' : ''}`}
            initial={skipAnimation ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={skipAnimation ? {} : { delay: rank * 0.05 }}
            onClick={onClick}
            whileHover={{ scale: 1.02 }}
        >
            <div className="vs-card-header">
                {getRankBadge()}
                <h4 className="vs-name">{voice_set_name}</h4>
            </div>

            <div className="vs-card-body">
                {/* Mini Donut Chart */}
                <div className="vs-chart">
                    <ResponsiveContainer width="100%" height={100}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={25}
                                outerRadius={40}
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const d = payload[0].payload
                                        return (
                                            <div className="vs-tooltip">
                                                <span style={{ color: d.color }}>{d.name}</span>
                                                <strong>{d.value} ({d.percentage}%)</strong>
                                            </div>
                                        )
                                    }
                                    return null
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="vs-chart-center">
                        <span className="vs-transfer-count">{formatCompact(transfers)}</span>
                        <span className="vs-transfer-label">xfers</span>
                    </div>
                </div>

                {/* Stats */}
                <div className="vs-stats">
                    <div className="vs-stat">
                        <span className="vs-stat-value">{formatCompact(total_calls)}</span>
                        <span className="vs-stat-label">Total Calls</span>
                    </div>
                    <div className="vs-stat highlight">
                        <span className="vs-stat-value">{transfer_rate}%</span>
                        <span className="vs-stat-label">Transfer Rate</span>
                    </div>
                    <div className="vs-stat">
                        <span className="vs-stat-value">{formatDuration(avg_duration)}</span>
                        <span className="vs-stat-label">Avg Duration</span>
                    </div>
                </div>
            </div>

            {/* Disposition Legend */}
            <div className="vs-legend">
                {pieData.slice(0, 4).map((item, i) => (
                    <div key={i} className="vs-legend-item">
                        <span className="vs-legend-dot" style={{ background: item.color }}></span>
                        <span className="vs-legend-text">{item.name}</span>
                        <span className="vs-legend-pct">{item.percentage}%</span>
                    </div>
                ))}
            </div>
        </motion.div>
    )
}